import {Vector} from "ol/source";
import VectorLayer from "ol/layer/Vector";
import {Style} from "ol/style";
import {Draw, Modify} from "ol/interaction";
import MapBrowserEventType from "ol/MapBrowserEventType";
import {createFeatureFromCoordinates} from "../utilities";
import {LayerControl} from "../controls/LayerControl";

export class EpLayer {
    constructor(map) {
        this.name = '';
        this.defaultVisibility = 'true';
        this.map = map;
    }

    setStyle(style, farStyle = null) {
        this.style = style;
        this.farStyle = farStyle ?? style;
    }

    getLayer() {
        if (!this.style) {
            this.style = new Style();
        }

        if (!this.layer) {
            let visible = (LayerControl.getLayerSavedVisibility(this.name) ?? this.defaultVisibility) === 'true';

            this.layer = new VectorLayer({
                source: this.getSource(),
                style: this.style,
                visible
            });

            this.layer.set('name', this.name)
        }

        return this.layer;
    }

    getSource() {
        if (!this.source) {
            this.source = new Vector();
        }

        return this.source;
    }

    getDraw() {
        return this.draw;
    }

    addDraw(options = {}) {
        options = {
            ...{
                type: 'Polygon',
                end: null,
                start: null,
                condition: undefined,
                style: this.style,
                maxPoints: undefined,
                noSource: false,
            }, ...options
        };

        this.draw = new Draw({
            source: options.noSource ? undefined : this.source,
            condition: options.condition,
            type: options.type,
            style: options.style,
            maxPoints: options.maxPoints,
        });

        if (options.end) {
            this.draw.on('drawend', options.end);
        }

        if (options.start) {
            this.draw.on('drawstart', options.start);
        }

        return this.draw;
    }

    addEdit(options = {}) {
        options = {
            ...{
                end: null,
                style: this.style,
                condition: undefined,
                features: [],
                insertVertexCondition: undefined,
            }, ...options
        };

        this.edit = new Modify({
            style: options.style,
            features: options.features,
            condition: options.condition,
            deleteCondition: (e) => {
                return e.type === MapBrowserEventType.SINGLECLICK;
            },
            insertVertexCondition: options.insertVertexCondition
        });

        if (options.end) {
            this.edit.on('modifyend', options.end);
        }

        return this.edit;
    }

    addFeature(coordinates, options = {}) {
        if (coordinates === null) {
            return;
        } else if (typeof coordinates != 'string') {
            coordinates = coordinates.join(' ');
        }

        options = {...{resize: false, fields: {}, type: 'Polygon'}, ...options};

        let feature = createFeatureFromCoordinates(coordinates, options.name, options.type);
        for (let field in options.fields) {
            if (field === 'id') {
                feature.setId(options.fields[field]);
            } else {
                feature.set(field, options.fields[field]);
            }
        }

        this.getSource().addFeature(feature);

        if (options.resize) {
            this.fitInView();
        }

        return feature;
    }

    fitInView(options = {}) {
        options = {...{padding: [24, 24, 24, 24], maxZoom: undefined}, ...options};

        // bugfix for no features
        let extent = this.getSource().getExtent();
        if(Math.abs(extent[0]) === Infinity) {
            return;
        }

        this.map.getView().fit(extent, options)
    }

    addFeaturesFromObject(features, options = {}) {
        let that = this;

        for(let feature of features) {
            that.addFeature(feature.coordinates, {
                resize: options.resize ?? false,
                fields: {
                    id: feature.id,
                    'feature-data': feature,
                    type: that.name ?? '',
                    zoomLimit: options.zoomLimit ?? 10,
                }
            });
        }
    }
}
